/**
 * Custom Groups Service
 * Handles all custom groups operations with proper error handling and validation
 */

const CUSTOM_GROUPS_STORAGE_KEY = 'customGroups';
const MAX_GROUP_NAME_LENGTH = 50;
const ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9\s]+$/;
const CUSTOM_GROUP_PREFIX = '_';

/**
 * Error class for custom group operations
 */
export class CustomGroupError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'CustomGroupError';
    this.code = code;
  }
}

/**
 * Generates a unique 4-character alphanumeric ID
 * @returns {string} A unique 4-character ID
 */
const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Storage operations wrapper with error handling
 */
const storage = {
  get: () => {
    try {
      if (!window.localStorage) {
        throw new CustomGroupError('LocalStorage is not available', 'STORAGE_UNAVAILABLE');
      }
      const stored = window.localStorage.getItem(CUSTOM_GROUPS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      if (error instanceof CustomGroupError) throw error;
      throw new CustomGroupError('Failed to read custom groups from storage', 'STORAGE_READ_ERROR');
    }
  },

  set: (groups) => {
    try {
      if (!window.localStorage) {
        throw new CustomGroupError('LocalStorage is not available', 'STORAGE_UNAVAILABLE');
      }
      window.localStorage.setItem(CUSTOM_GROUPS_STORAGE_KEY, JSON.stringify(groups));
    } catch (error) {
      if (error instanceof CustomGroupError) throw error;
      throw new CustomGroupError('Failed to save custom groups to storage', 'STORAGE_WRITE_ERROR');
    }
  },

  clear: () => {
    try {
      if (!window.localStorage) {
        throw new CustomGroupError('LocalStorage is not available', 'STORAGE_UNAVAILABLE');
      }
      window.localStorage.removeItem(CUSTOM_GROUPS_STORAGE_KEY);
    } catch (error) {
      if (error instanceof CustomGroupError) throw error;
      throw new CustomGroupError('Failed to clear custom groups from storage', 'STORAGE_CLEAR_ERROR');
    }
  },
};

/**
 * Validation utilities
 */
const validation = {
  validateGroupName: (name) => {
    if (!name || typeof name !== 'string') {
      return { isValid: false, error: 'Group name is required' };
    }

    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      return { isValid: false, error: 'Group name cannot be empty' };
    }

    if (trimmedName.length > MAX_GROUP_NAME_LENGTH) {
      return { isValid: false, error: `Group name must be ${MAX_GROUP_NAME_LENGTH} characters or less` };
    }

    if (!ALLOWED_CHARS_REGEX.test(trimmedName)) {
      return { isValid: false, error: 'Group name can only contain letters, numbers, and spaces' };
    }

    return { isValid: true, error: null };
  },

  validateCountries: (countries) => {
    if (!Array.isArray(countries)) {
      return { isValid: false, error: 'Countries must be an array' };
    }

    if (countries.length < 2) {
      return { isValid: false, error: 'A custom group must contain at least 2 countries' };
    }

    // Validate each country code
    const invalidCountries = countries.filter((code) => {
      return typeof code !== 'string' || !/^[A-Z]{2,3}$/.test(code);
    });

    if (invalidCountries.length > 0) {
      return { isValid: false, error: 'Invalid country codes detected' };
    }

    return { isValid: true, error: null };
  },
};

/**
 * URL encoding/decoding utilities
 */
const urlUtils = {
  encodeGroupName: (name) => {
    // Keep it simple - the name is already validated to contain only alphanumeric + spaces
    return name.trim();
  },

  decodeGroupName: (encodedName) => {
    if (!encodedName) return '';
    try {
      return decodeURIComponent(encodedName.replace(/\+/g, '%20'));
    } catch (error) {
      return encodedName.replace(/\+/g, ' ');
    }
  },

  generateUrlParam: (groupId, groupName, countries) => {
    const encodedName = urlUtils.encodeGroupName(groupName);
    const countryString = countries.join('_');
    return `${CUSTOM_GROUP_PREFIX}${groupId}_${encodedName}_${countryString}`;
  },

  parseUrlParam: (param) => {
    if (!param || typeof param !== 'string') return null;

    if (!param.startsWith(CUSTOM_GROUP_PREFIX)) {
      return null;
    }

    const payload = param.slice(CUSTOM_GROUP_PREFIX.length);
    const parts = payload.split('_');
    if (parts.length < 3) return null;

    const [id, encodedName, ...countryParts] = parts;
    const name = urlUtils.decodeGroupName(encodedName);
    const countries = countryParts.filter((c) => c.length > 0);
    return { id, name, countries };
  },

  isCustomGroupParam: (param) => typeof param === 'string' && param.startsWith(CUSTOM_GROUP_PREFIX),
};

/**
 * Main service object with all custom groups operations
 */
const customGroupsService = {
  /**
   * Gets all custom groups
   * @returns {Promise<Object>} Object containing all custom groups
   */
  getAll: async () => {
    try {
      return storage.get();
    } catch (error) {
      console.error('Error getting custom groups:', error);
      throw error;
    }
  },

  /**
   * Gets a specific custom group by ID
   * @param {string} id - The group ID
   * @returns {Promise<Object|null>} The group object or null if not found
   */
  getById: async (id) => {
    try {
      const groups = storage.get();
      return groups[id] || null;
    } catch (error) {
      console.error('Error getting custom group by ID:', error);
      throw error;
    }
  },

  /**
   * Gets a specific custom group by name
   * @param {string} name - The group name
   * @returns {Promise<Object|null>} The group object or null if not found
   */
  getByName: async (name) => {
    try {
      const groups = storage.get();
      return Object.values(groups).find((group) => group.name === name) || null;
    } catch (error) {
      console.error('Error getting custom group by name:', error);
      throw error;
    }
  },

  /**
   * Creates a new custom group
   * @param {string} name - The group name
   * @param {Array<string>} countries - Array of country ISO codes
   * @returns {Promise<Object>} The created group with its ID
   */
  create: async (name, countries) => {
    try {
      // Validate inputs
      const nameValidation = validation.validateGroupName(name);
      if (!nameValidation.isValid) {
        throw new CustomGroupError(nameValidation.error, 'VALIDATION_ERROR');
      }

      const countriesValidation = validation.validateCountries(countries);
      if (!countriesValidation.isValid) {
        throw new CustomGroupError(countriesValidation.error, 'VALIDATION_ERROR');
      }

      const groups = storage.get();
      const trimmedName = name.trim();

      // Check if name already exists
      const existingGroup = Object.values(groups).find((g) => g.name === trimmedName);
      if (existingGroup) {
        throw new CustomGroupError('A group with this name already exists', 'DUPLICATE_NAME');
      }

      // Generate unique ID
      const existingIds = Object.values(groups).map((g) => g.id);
      let finalId = generateUniqueId();
      while (existingIds.includes(finalId)) {
        finalId = generateUniqueId();
      }

      // Create new group
      const newGroup = {
        id: finalId,
        name: trimmedName,
        countries: [...countries], // Create a copy to prevent mutations
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      };

      groups[finalId] = newGroup;
      storage.set(groups);

      return newGroup;
    } catch (error) {
      console.error('Error creating custom group:', error);
      throw error;
    }
  },

  /**
   * Updates an existing custom group
   * @param {string} id - The group ID to update
   * @param {string} name - The new group name
   * @param {Array<string>} countries - Array of country ISO codes
   * @returns {Promise<Object>} The updated group
   */
  update: async (id, name, countries) => {
    try {
      // Validate inputs
      const nameValidation = validation.validateGroupName(name);
      if (!nameValidation.isValid) {
        throw new CustomGroupError(nameValidation.error, 'VALIDATION_ERROR');
      }

      const countriesValidation = validation.validateCountries(countries);
      if (!countriesValidation.isValid) {
        throw new CustomGroupError(countriesValidation.error, 'VALIDATION_ERROR');
      }

      const groups = storage.get();

      if (!groups[id]) {
        throw new CustomGroupError('Group not found', 'NOT_FOUND');
      }

      const trimmedName = name.trim();

      // Check if new name conflicts with another group
      const existingGroup = Object.values(groups).find((g) => g.name === trimmedName && g.id !== id);
      if (existingGroup) {
        throw new CustomGroupError('A group with this name already exists', 'DUPLICATE_NAME');
      }

      // Update group
      groups[id] = {
        ...groups[id],
        name: trimmedName,
        countries: [...countries], // Create a copy to prevent mutations
        updated: new Date().toISOString(),
      };

      storage.set(groups);
      return groups[id];
    } catch (error) {
      console.error('Error updating custom group:', error);
      throw error;
    }
  },

  /**
   * Deletes a custom group
   * @param {string} id - The group ID to delete
   * @returns {Promise<boolean>} True if deleted successfully
   */
  delete: async (id) => {
    try {
      const groups = storage.get();

      if (!groups[id]) {
        throw new CustomGroupError('Group not found', 'NOT_FOUND');
      }

      delete groups[id];
      storage.set(groups);

      return true;
    } catch (error) {
      console.error('Error deleting custom group:', error);
      throw error;
    }
  },

  /**
   * Creates a custom group from URL parameters (for sharing)
   * @param {string} groupId - The group ID
   * @param {string} groupName - The group name
   * @param {Array<string>} countries - Array of country ISO codes
   * @returns {Promise<Object>} The created/existing group
   */
  createFromUrl: async (groupId, groupName, countries) => {
    try {
      const groups = storage.get();

      // If group already exists, return it
      if (groups[groupId]) {
        return groups[groupId];
      }

      // Validate inputs
      const nameValidation = validation.validateGroupName(groupName);
      if (!nameValidation.isValid) {
        throw new CustomGroupError(nameValidation.error, 'VALIDATION_ERROR');
      }

      const countriesValidation = validation.validateCountries(countries);
      if (!countriesValidation.isValid) {
        throw new CustomGroupError(countriesValidation.error, 'VALIDATION_ERROR');
      }

      // Create new group with specified ID
      const newGroup = {
        id: groupId,
        name: groupName.trim(),
        countries: [...countries],
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        fromUrl: true, // Mark as imported from URL
      };

      groups[groupId] = newGroup;
      storage.set(groups);

      return newGroup;
    } catch (error) {
      console.error('Error creating custom group from URL:', error);
      throw error;
    }
  },

  /**
   * Clears all custom groups
   * @returns {Promise<void>}
   */
  clearAll: async () => {
    try {
      storage.clear();
    } catch (error) {
      console.error('Error clearing custom groups:', error);
      throw error;
    }
  },

  // Export utility functions for external use
  validation,
  urlUtils,
  constants: {
    MAX_GROUP_NAME_LENGTH,
    ALLOWED_CHARS_REGEX,
    CUSTOM_GROUP_PREFIX,
  },
};

export default customGroupsService;
